import React, { useState, useMemo, useEffect } from 'react';
import { useRugbyTracker } from '../../context/RugbyTrackerContext';
import { useAuth } from '../../context/AuthContext';
import { useDatabase } from '../../context/DatabaseContext';
import { getFieldZone } from '../../constants/rugbyConstants';
import { exportEventsToCsv } from '../../utils/csvExport';
import { eventHasAnyTag } from '../../utils/tagFilterUtils';
import { format } from 'date-fns';

import {
  ScoreSummary,
  EventDistribution,
  ZoneAnalysis,
  PeriodDistribution,
  HeatmapView,
  CommonEvents,
  TeamEventComparison,
  EventsView
} from './components';

// Import shared stats section styles
import './components/StatsSection.css';
// Import page-specific styles
import './AnalysisPage.css';

const AnalysisPage = ({ toggleSidebar, showToast }) => {
    // Get contexts
    const { state: contextState } = useRugbyTracker();
    const { user } = useAuth();
    const { getMatches, getMatchById } = useDatabase();

    // Component state
    const [viewMode, setViewMode] = useState('summary');
    const [matchList, setMatchList] = useState(() => {
        // Initialize from localStorage if available
        const savedList = localStorage.getItem('analysis_match_list');
        return savedList ? JSON.parse(savedList) : [];
    });
    const [selectedMatchId, setSelectedMatchId] = useState(() => {
        // Initialize from localStorage if available
        return localStorage.getItem('analysis_selected_match_id') || 'current';
    });
    const [isLoading, setIsLoading] = useState(false);
    const [loadedMatchData, setLoadedMatchData] = useState(() => {
        // Initialize from localStorage if available
        const savedData = localStorage.getItem('analysis_loaded_match_data');
        return savedData ? JSON.parse(savedData) : null;
    });
    
    // Event filtering state (lifted up from EventsView)
    const [filters, setFilters] = useState({
        eventType: '',
        team: '',
        period: '',
        zone: '',
        tags: []
    });

    // Effect to persist state to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('analysis_match_list', JSON.stringify(matchList));
    }, [matchList]);

    useEffect(() => {
        localStorage.setItem('analysis_selected_match_id', selectedMatchId);
    }, [selectedMatchId]);

    useEffect(() => {
        localStorage.setItem('analysis_loaded_match_data', JSON.stringify(loadedMatchData));
    }, [loadedMatchData]);

    // Load match data on mount if a match was previously selected
    useEffect(() => {
        const loadSelectedMatch = async () => {
            // If a match is selected (not 'current') but no data is loaded, fetch it
            if (selectedMatchId !== 'current' && !loadedMatchData && getMatchById) {
                setIsLoading(true);
                try {
                    const matchData = await getMatchById(selectedMatchId);
                    if (matchData) {
                        setLoadedMatchData(matchData);
                    }
                } catch (error) {
                    console.error("Error loading previously selected match:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        loadSelectedMatch();
    }, [selectedMatchId, loadedMatchData, getMatchById]);

    // Fetch match list on mount if user is logged in
    useEffect(() => {
        const fetchMatches = async () => {
            if (user && getMatches) {
                try {
                    const allMatches = await getMatches();
                    const userMatches = (allMatches || []).filter(match => match.user_id === user.id);
                    const sortedMatches = userMatches.sort((a, b) =>
                        new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at)
                    );
                    setMatchList(sortedMatches);
                } catch (error) {
                    console.error("Error fetching matches:", error);
                    showToast("Failed to load match list", "error");
                    setMatchList([]);
                }
            } else {
                setMatchList([]);
            }
        };

        fetchMatches();
    }, [user, getMatches, showToast]);

    // Determine which data to use: loaded match data or live context data
    const currentEvents = useMemo(() => {
        if (loadedMatchData && loadedMatchData.events) {
            // Format events from loaded match data
            return loadedMatchData.events.map(event => ({
                id: event.id,
                eventType: event.event_type,
                team: event.team,
                playerNumber: event.player_number,
                timestamp: event.timestamp,
                seconds: event.seconds,
                period: event.period,
                position: {
                    x: event.position_x,
                    y: event.position_y
                },
                comment: event.comment,
                zone: event.zone
            }));
        }
        // Use live context data if no loaded data
        return contextState.events;
    }, [loadedMatchData, contextState.events]);
    
    // Apply filters to events
    const filteredEvents = useMemo(() => {
        return (currentEvents || []).filter(event => {
            if (filters.eventType && event.eventType !== filters.eventType) return false;
            if (filters.team && event.team !== filters.team) return false;
            if (filters.period && event.period !== filters.period) return false;
            
            // Handle zone filtering with period consideration
            if (filters.zone) {
                const eventZone = event.zone || getFieldZone(event.position.x, event.period);
                if (eventZone !== filters.zone) return false;
            }
            
            // Handle tag filtering
            if (filters.tags && filters.tags.length > 0) {
                if (!eventHasAnyTag(event, filters.tags)) return false;
            }
            
            return true;
        });
    }, [currentEvents, filters]);
    
    // Get unique periods from events
    const periods = useMemo(() => {
        return [...new Set(currentEvents.map(event => event.period))].filter(Boolean);
    }, [currentEvents]);
    
    // Reset filters function
    const resetFilters = () => {
        setFilters({
            eventType: '',
            team: '',
            period: '',
            zone: '',
            tags: []
        });
    };

    // Determine team names to use
    const currentHomeTeamName = loadedMatchData ? 
        (loadedMatchData.home_team_name || loadedMatchData.home_team?.name || 'Home') : 
        contextState.homeTeamName;
    
    const currentAwayTeamName = loadedMatchData ? 
        (loadedMatchData.away_team_name || loadedMatchData.away_team?.name || 'Away') : 
        contextState.awayTeamName;

    // Use context's EVENT_TYPES for consistency
    const { EVENT_TYPES } = contextState;

    // Define score events constant with useMemo to prevent unnecessary re-renders
    const SCORE_EVENTS = useMemo(() => ({
        'Try': 5,
        'Penalty Try': 7, // Includes automatic conversion
        'Conversion': 2,
        'Penalty Goal': 3,
        'Drop Goal': 3
    }), []);

    // Calculate statistics from events
    const stats = useMemo(() => {
        // Initialize stats object
        const stats = {
            homeEvents: 0,
            awayEvents: 0,
            eventCounts: {},
            territorySplit: {
                home: { attacking: 0, defending: 0 },
                away: { attacking: 0, defending: 0 }
            },
            periodEvents: {
                'First Half': 0,
                'Second Half': 0,
                'Overtime': 0
            },
            scores: {
                home: 0,
                away: 0
            },
            // Add zone statistics
            zoneStats: {
                'Red Zone': { home: 0, away: 0, total: 0 },
                'Yellow Zone': { home: 0, away: 0, total: 0 },
                'Green Zone': { home: 0, away: 0, total: 0 }
            }
        };

        // Initialize event counts
        EVENT_TYPES.forEach(type => {
            stats.eventCounts[type] = { home: 0, away: 0, total: 0 };
        });

        // Process events for statistics
        (currentEvents || []).forEach(event => {
            // Count by team
            if (event.team === 'Home') {
                stats.homeEvents++;
                // Update scores
                if (SCORE_EVENTS[event.eventType]) {
                    stats.scores.home += SCORE_EVENTS[event.eventType];
                }
            } else {
                stats.awayEvents++;
                // Update scores
                if (SCORE_EVENTS[event.eventType]) {
                    stats.scores.away += SCORE_EVENTS[event.eventType];
                }
            }

            // Count by event type
            if (stats.eventCounts[event.eventType]) { // Guard against unknown event types
               stats.eventCounts[event.eventType].total++;
               stats.eventCounts[event.eventType][event.team.toLowerCase()]++;
            }

            // Territory calculation (simplified)
            if (event.position) { // Guard against missing position
                let adjustedX = event.position.x;
                // Adjust for period
                if (event.period === 'Second Half' || event.period === 'Overtime') {
                    adjustedX = 1 - adjustedX; // Invert for second half/overtime
                }
                
                if (event.team === 'Home') {
                    if (adjustedX < 0.5) {
                        stats.territorySplit.home.defending++;
                    } else {
                        stats.territorySplit.home.attacking++;
                    }
                } else {
                    if (adjustedX < 0.5) {
                        stats.territorySplit.away.attacking++;
                    } else {
                        stats.territorySplit.away.defending++;
                    }
                }
            }

            // Period events
            if (event.period && stats.periodEvents[event.period] !== undefined) { // Guard against unknown periods
                stats.periodEvents[event.period]++;
            }

            // Zone statistics
            const zone = event.zone || (event.position ? getFieldZone(event.position.x) : null); // Guard position access
            if (zone && stats.zoneStats[zone]) { // Guard against unknown zones
                stats.zoneStats[zone].total++;
                if (event.team === 'Home') {
                    stats.zoneStats[zone].home++;
                } else {
                    stats.zoneStats[zone].away++;
                }
            }
        });

        // Add percentage calculations
        const totalEvents = (currentEvents || []).length;
        if (totalEvents > 0) {
            stats.homePercentage = Math.round((stats.homeEvents / totalEvents) * 100);
            stats.awayPercentage = Math.round((stats.awayEvents / totalEvents) * 100);

            // Calculate home team territory percentage
            const homeTotalActions = stats.territorySplit.home.attacking + stats.territorySplit.home.defending;
            if (homeTotalActions > 0) {
                stats.territorySplit.home.attackingPercentage = Math.round(
                    (stats.territorySplit.home.attacking / homeTotalActions) * 100
                );
                stats.territorySplit.home.defendingPercentage = 100 - stats.territorySplit.home.attackingPercentage;
            } else {
                 stats.territorySplit.home.attackingPercentage = 0;
                 stats.territorySplit.home.defendingPercentage = 0;
            }

            // Calculate away team territory percentage
            const awayTotalActions = stats.territorySplit.away.attacking + stats.territorySplit.away.defending;
            if (awayTotalActions > 0) {
                stats.territorySplit.away.attackingPercentage = Math.round(
                    (stats.territorySplit.away.attacking / awayTotalActions) * 100
                );
                stats.territorySplit.away.defendingPercentage = 100 - stats.territorySplit.away.attackingPercentage;
            } else {
                 stats.territorySplit.away.attackingPercentage = 0;
                 stats.territorySplit.away.defendingPercentage = 0;
            }

            // Calculate zone percentages
            Object.keys(stats.zoneStats).forEach(zone => {
                const zoneTotal = stats.zoneStats[zone].total;
                if (zoneTotal > 0) {
                    stats.zoneStats[zone].homePercentage =
                        Math.round((stats.zoneStats[zone].home / zoneTotal) * 100);
                    stats.zoneStats[zone].awayPercentage =
                        Math.round((stats.zoneStats[zone].away / zoneTotal) * 100);
                } else {
                    stats.zoneStats[zone].homePercentage = 0;
                    stats.zoneStats[zone].awayPercentage = 0;
                }
            });
        } else {
             // Default percentages if no events
             stats.homePercentage = 0;
             stats.awayPercentage = 0;
             stats.territorySplit.home.attackingPercentage = 0;
             stats.territorySplit.home.defendingPercentage = 0;
             stats.territorySplit.away.attackingPercentage = 0;
             stats.territorySplit.away.defendingPercentage = 0;
             Object.keys(stats.zoneStats).forEach(zone => {
                 stats.zoneStats[zone].homePercentage = 0;
                 stats.zoneStats[zone].awayPercentage = 0;
             });
        }

        return stats;
    }, [currentEvents, EVENT_TYPES, SCORE_EVENTS]); // Update dependency array to use currentEvents

    // Get most common event types
    const topEvents = useMemo(() => {
        return Object.entries(stats.eventCounts)
            .map(([type, counts]) => ({ type, ...counts }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 5);
    }, [stats]);

    // Function to export events data as CSV using the utility function
    const handleExportToCSV = () => {
        exportEventsToCsv(currentEvents, currentHomeTeamName, currentAwayTeamName, showToast);
    };

    // --- Handler for dropdown change ---
    const handleDropdownChange = (event) => {
        setSelectedMatchId(event.target.value);
    };

    // --- Handler for Load button click ---
    const handleLoadClick = async () => {
        if (selectedMatchId === 'current') {
            // Clear loaded match data to show current match
            setLoadedMatchData(null);
            showToast("Switched back to current match data", "info");
        } else {
            // Load selected match details
            if (!getMatchById) {
                 showToast("Database function not available", "error");
                 return;
            }
            setIsLoading(true);
            try {
                const matchData = await getMatchById(selectedMatchId); // Fetch full match data
                if (matchData) {
                    // Store match data in local state instead of updating context
                    setLoadedMatchData(matchData);
                    showToast(`Loaded match: ${matchData.home_team_name || 'Home'} vs ${matchData.away_team_name || 'Away'}`, "success");
                } else {
                     showToast("Could not find match data", "error");
                }
            } catch (error) {
                console.error("Error loading match:", error);
                showToast("Failed to load selected match", "error");
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="analysis-page">
            {/* Header */}
            <header className="analysis-header">
                <h1 className="app-title">Match Analysis</h1>

                {/* Match Loader Section */}
                <div className="match-loader">
                    <select 
                        value={selectedMatchId} 
                        onChange={handleDropdownChange} 
                        disabled={!user || isLoading}
                        aria-label="Select match"
                    >
                        <option value="current">Current Match</option>
                        {matchList.map((match) => (
                            <option key={match.id} value={match.id}>
                                {`${match.home_team_name || 'Home'} vs ${match.away_team_name || 'Away'} - ${format(new Date(match.timestamp || match.created_at), 'dd-MM-yy')}`}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={handleLoadClick}
                        disabled={isLoading || !user} // Disable while loading or if not logged in
                        title={!user ? "Log in to load matches" : ""}
                    >
                        {isLoading ? 'Loading...' : 'Load Game Data'}
                    </button>
                </div>

                <button
                    className="export-btn"
                    onClick={handleExportToCSV}
                    disabled={!currentEvents || currentEvents.length === 0}
                    title={(!currentEvents || currentEvents.length === 0) ? "No data to export" : "Export as CSV"}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Export CSV
                </button>

                {/* View mode tabs */}
                <div className="analysis-tabs">
                    <button
                        className={`tab-button ${viewMode === 'summary' ? 'active' : ''}`}
                        onClick={() => setViewMode('summary')}
                    >
                        Summary
                    </button>
                    <button
                        className={`tab-button ${viewMode === 'heatmap' ? 'active' : ''}`}
                        onClick={() => setViewMode('heatmap')}
                    >
                        Heatmap
                    </button>
                    <button
                        className={`tab-button ${viewMode === 'events' ? 'active' : ''}`}
                        onClick={() => setViewMode('events')}
                    >
                        Events
                    </button>
                </div>
            </header>

            {/* Stats content */}
            <div className="analysis-content">
                {(!currentEvents || currentEvents.length === 0) ? (
                    <div className="no-data-message">
                        <p>No event data available for analysis.</p>
                        <p>Record events during a match or load a saved match to see statistics.</p>
                    </div>
                ) : (
                    <div className="stats-container">
                        {viewMode === 'summary' && (
                            <>
                                <ScoreSummary
                                    homeTeamName={currentHomeTeamName}
                                    awayTeamName={currentAwayTeamName}
                                    scores={stats.scores}
                                />

                                <EventDistribution
                                    homeTeamName={currentHomeTeamName}
                                    awayTeamName={currentAwayTeamName}
                                    homeEvents={stats.homeEvents}
                                    awayEvents={stats.awayEvents}
                                    homePercentage={stats.homePercentage}
                                    awayPercentage={stats.awayPercentage}
                                />

                                <ZoneAnalysis
                                    homeTeamName={currentHomeTeamName}
                                    awayTeamName={currentAwayTeamName}
                                    zoneStats={stats.zoneStats}
                                />

                                <PeriodDistribution
                                    periodEvents={stats.periodEvents}
                                    totalEvents={currentEvents.length}
                                />
                            </>
                        )}

                        {viewMode === 'heatmap' && (
                            <HeatmapView
                                events={currentEvents}
                                homeTeamName={currentHomeTeamName}
                                awayTeamName={currentAwayTeamName}
                                territorySplit={stats.territorySplit}
                            />
                        )}

                        {viewMode === 'events' && (
                            <>
                                <CommonEvents
                                    topEvents={topEvents}
                                    homeTeamName={currentHomeTeamName}
                                    awayTeamName={currentAwayTeamName}
                                />
                                <TeamEventComparison
                                    filteredEvents={filteredEvents}
                                    homeTeamName={currentHomeTeamName}
                                    awayTeamName={currentAwayTeamName}
                                    EVENT_TYPES={EVENT_TYPES}
                                />
                                <EventsView
                                    events={currentEvents}
                                    filteredEvents={filteredEvents}
                                    homeTeamName={currentHomeTeamName}
                                    awayTeamName={currentAwayTeamName}
                                    EVENT_TYPES={EVENT_TYPES}
                                    filters={filters}
                                    setFilters={setFilters}
                                    periods={periods}
                                    resetFilters={resetFilters}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalysisPage;